<template>
  <v-card>
    <div class="pa-6">
      <div class="d-flex justify-end align-center pb-6">
        <v-spacer></v-spacer>
        <v-menu open-on-hover bottom offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn class="my-0" color="green" dark v-bind="attrs" v-on="on" outlined> Nhập/xuất Excel </v-btn>
          </template>

          <v-list>
            <v-list-item class="cursor-pointer">
              <v-list-item-title @click="onUploadClick">Tải lên tệp Excel chứa biên bản</v-list-item-title>
              <input
                ref="uploader"
                type="file"
                class="d-none"
                accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                @change="onFileChanged"
              />
            </v-list-item>
            <v-list-item class="cursor-pointer">
              <v-list-item-title @click="exportDataToExcel">Tải xuống tệp Excel mẫu</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
      <v-card>
        <ConfirmAbsentDialog
          :state="confirmMarkDialog"
          :importedDocuments="importedDocuments"
          :examId="query.exam"
          @closeDialog="closeConfirmDialog"
          @updateResult="onUpdateResultDocument"
          @refresh="refresh"
          ref="absentDialog"
          :all="true"
        />
        <v-data-table
          item-key="id"
          :headers="computedHeaders"
          :loading="loading"
          :items="marks"
          :footer-props="{ 'items-per-page-text': 'Số học sinh mỗi trang' }"
          v-bind="this.$attrs"
          fixed-header
        >
          <template v-for="subject in this.subjectsToUse" v-slot:[`item.subject_${subject.id}`]="{ item }">
            <v-tooltip bottom :key="subject.id">
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">
                  {{ item[`subject_${subject.id}`] }}
                </span>
              </template>
              <div>
                {{ item[`note_${subject.id}`] }}
              </div>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-card>
    </div>
  </v-card>
</template>

<script>
import { mapActions } from 'vuex'
import readXlsxFile from 'read-excel-file'
import ConfirmAbsentDialog from './ConfirmAbsentDialog.vue'
import { ExamMark } from '../../plugins/api'
import * as XLSX from 'xlsx'

const originHeaders = [
  {
    text: 'Học sinh',
    value: 'studentName',
    align: 'left',
    sortable: false,
    width: '15%'
  },
  {
    text: 'Số báo danh',
    value: 'examCode',
    align: 'left',
    sortable: false,
    width: '10%'
  },
  {
    text: 'Phòng thi',
    value: 'rooms',
    align: 'left',
    sortable: false,
    width: '10%'
  }
]

export default {
  props: {
    room: Object,
    query: Object,
    teacher: Object,
    subjects: Array
  },
  data() {
    return {
      loading: false,
      headers: originHeaders,
      confirmMarkDialog: false,
      importedDocuments: [],
      subjectHeaders: [],
      marks: [],
      json_fields: {
        'Số báo danh': 'examCode',
        'Họ tên': 'name',
        'Mã HS': 'studentCode',
        'Phòng thi': 'room'
      },
      markMapping: {
        absent: 'Vắng thi',
        present: 'Có thi',
        emtpy: 'Không biên bản',
        suspended: 'VPQC'
      }
    }
  },
  components: { ConfirmAbsentDialog },
  computed: {
    computedHeaders() {
      if (this.$auth.role.type === 'teacher') {
        const teacherSubjects = this.teacher.subjects.map(subject => subject.id)
        const filteredSubjectHeaders = this.subjectHeaders.filter(header =>
          teacherSubjects.includes(header.value.split('_')[1])
        )
        return [...this.headers, ...filteredSubjectHeaders]
      }
      return [...this.headers, ...this.subjectHeaders]
    },
    subjectsToUse() {
      return this.room ? this.room.subjects : this.subjects
    }
  },
  mounted() {
    this.updateSubjectHeaders(this.room)
  },
  methods: {
    ...mapActions('examMark', ['fetchMarks', 'countMarks']),
    async refresh() {
      this.$loading.active = true
      this.updateSubjectHeaders(this.room)
      this.loading = true
      try {
        const rawMarks = await this.fetchMarks({
          ...this.query,
          _limit: -1,
          _sort: 'examCode:ASC'
        })
        this.marks = this.transformMarksData(rawMarks, this.subjectsToUse)
      } catch (error) {
        console.log(error)
        this.$alert.error('Lỗi load thông tin học sinh')
      } finally {
        this.loading = false
        this.$loading.active = false
      }
    },
    updateSubjectHeaders(room) {
      const subjects = room ? room.subjects : this.subjects
      this.subjectHeaders = subjects.map(subject => ({
        text: subject.name,
        value: `subject_${subject.id}`,
        align: 'left',
        sortable: false,
        width: '10%'
      }))
    },

    transformMarksData(marks, subjects) {
      const studentData = {}

      marks.forEach(mark => {
        if (!studentData[mark.student.id]) {
          studentData[mark.student.id] = {
            studentName: mark.student.name,
            dob: mark.student.dob,
            examCode: mark.examCode,
            rooms: new Set(),
            ...subjects.reduce(
              (acc, subject) => ({
                ...acc,
                [`subject_${subject.id}`]: '-',
                [`note_${subject.id}`]: ''
              }),
              {}
            )
          }
        }

        studentData[mark.student.id][`subject_${mark.subject.id}`] = this.markMapping[mark.status]
        studentData[mark.student.id][`note_${mark.subject.id}`] = mark.note
        studentData[mark.student.id].rooms.add(mark.room.name)
      })

      Object.keys(studentData).forEach(studentId => {
        studentData[studentId].rooms = Array.from(studentData[studentId].rooms).join(', ')
      })

      return Object.values(studentData)
    },
    onUploadClick() {
      this.isSelecting = true
      window.addEventListener(
        'focus',
        () => {
          this.isSelecting = false
        },
        { once: true }
      )
      this.$refs.uploader.click()
    },
    async onFileChanged(e) {
      this.$loading.active = true
      this.selectedFile = e.target.files[0]
      let rows, headers
      try {
        rows = await readXlsxFile(this.selectedFile)

        headers = rows[0]
        rows = rows.slice(1)
      } catch (error) {
        console.error(error)
        this.$alert.error('File không có định dạng .xlsx hoặc có dữ liệu không hợp lệ (tham khảo mẫu file)')
        this.$loading.active = false
        return
      }
      const subjectColumns = headers.reduce((acc, header, index) => {
        if (header.startsWith('Môn thi')) {
          const subjectId = header.replace(/^Môn thi\s*/, '')
          acc.push({ type: 'subject', index, subjectId })
        } else if (header.startsWith('Ghi chú')) {
          const subjectId = header.replace(/^Ghi chú\s*/, '')
          acc.push({ type: 'note', index, subjectId })
        }
        return acc
      }, [])

      const data = rows.map(row => {
        const student = {
          name: row[headers.indexOf('Họ tên')],
          examCode: row[headers.indexOf('Số báo danh')],
          rooms: row[headers.indexOf('Phòng thi')]
        }

        subjectColumns.forEach(col => {
          if (col.type === 'subject') {
            student[`Môn thi ${col.subjectId}`] = row[col.index]
          } else if (col.type === 'note') {
            student[`Ghi chú ${col.subjectId}`] = row[col.index]
          }
        })

        return student
      })
      this.importedDocuments = data
      this.confirmMarkDialog = true
      this.$loading.active = false
      this.$alert.success('Đã đọc File thành công!')
    },
    closeConfirmDialog() {
      this.confirmMarkDialog = false
      this.$refs.uploader.value = null
    },
    async onUpdateResultDocument(data) {
      this.$refs.uploader.value = null
      const updated = await ExamMark.updateAbsentExcel(this.query.exam, { updateDatas: data })
      if (updated) {
        this.$alert.success('Cập nhật biên bản thành công')
        this.closeConfirmDialog()
        this.refresh()
      } else {
        this.$alert.error('Lỗi cập nhật biên bản')
      }
    },
    async exportDataToExcel() {
      this.loading = true
      try {
        const data = await ExamMark.getAbsentExcel({ exam: this.query.exam })

        const formattedData = this.mapFields(data, this.json_fields)
        this.addDynamicFields(formattedData, data)

        const worksheet = XLSX.utils.json_to_sheet(formattedData)
        this.setColumnWidths(worksheet, formattedData)

        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Marks')

        XLSX.writeFile(workbook, `bien_ban.xlsx`)
      } catch (error) {
        console.error('Error exporting data to Excel:', error)
      } finally {
        this.loading = false
      }
    },
    mapFields(data, fieldMap) {
      return data.map(item => {
        const newItem = {}
        for (const [key, value] of Object.entries(fieldMap)) {
          newItem[key] = item[value]
        }
        return newItem
      })
    },
    addDynamicFields(formattedData, data) {
      if (data.length > 0) {
        const additionalFields = Object.keys(data[0]).filter(key => !Object.values(this.json_fields).includes(key))
        formattedData.forEach((item, index) => {
          additionalFields.forEach(field => {
            item[field] = data[index][field]
          })
        })
      }
    },
    setColumnWidths(worksheet, data) {
      let maxWidths = []
      const columnNames = data.length > 0 ? Object.keys(data[0]) : []

      columnNames.forEach((name, index) => {
        maxWidths[index] = name.length
      })

      data.forEach(row => {
        columnNames.forEach((name, index) => {
          const value = (row[name] || '').toString().trim()
          maxWidths[index] = Math.max(maxWidths[index], value.length)
        })
      })

      worksheet['!cols'] = maxWidths.map(width => {
        return { width }
      })
    }
  },
  watch: {
    query: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.refresh()
        }
      }
    }
  }
}
</script>
